import React from 'react';
import { Modal, Table, Badge, Button } from 'react-bootstrap';
import { useDeletePhantom } from '../../features/bobins/hooks/usePhantoms';

const fmt = n => Number(n || 0).toLocaleString();
const fmtDate = ts => ts ? new Date(ts).toLocaleString() : '—';

const PhantomTokenDetailModal = ({ show, onHide, phantom }) => {
  const deleteMutation = useDeletePhantom();

  if (!phantom) return null;

  const handleDelete = async () => {
    if (!window.confirm('Delete this phantom token?')) return;
    try {
      await deleteMutation.mutateAsync(phantom.phantomId);
      onHide();
    } catch (e) {
      alert(e?.message || 'Failed to delete');
    }
  };

  const wireDiamStr = Array.isArray(phantom.wireDiameter)
    ? phantom.wireDiameter.join(', ')
    : phantom.wireDiameter || '—';

  const wireCountStr = Array.isArray(phantom.numberOfWires)
    ? phantom.numberOfWires.join(', ')
    : phantom.numberOfWires || '—';

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton className="bg-body-tertiary">
        <Modal.Title className="d-flex align-items-center gap-2">
          Phantom Token
          <Badge bg="info">{phantom.phantomId}</Badge>
          <Badge bg={phantom.status === 'AVAILABLE' ? 'success' : 'secondary'}>
            {phantom.status}
          </Badge>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Table bordered size="sm" className="mb-0">
          <tbody>
            <tr>
              <th style={{ width: '40%' }}>ID</th>
              <td className="font-monospace">{phantom.phantomId}</td>
            </tr>
            <tr>
              <th>Phase</th>
              <td>
                <Badge bg="primary">{phantom.phase?.replaceAll('_', ' ')}</Badge>
              </td>
            </tr>
            <tr>
              <th>Available Length</th>
              <td className="fw-bold">{fmt(phantom.availableLength)} m</td>
            </tr>
            <tr>
              <th>Material</th>
              <td>{phantom.material || '—'}</td>
            </tr>
            <tr>
              <th>Wire Type</th>
              <td>{phantom.wireType || '—'}</td>
            </tr>
            <tr>
              <th>Wire Diameter (mm)</th>
              <td>{wireDiamStr}</td>
            </tr>
            <tr>
              <th>Number of Wires</th>
              <td>{wireCountStr}</td>
            </tr>
            <tr>
              <th>Section (mm2)</th>
              <td>{phantom.section != null ? phantom.section : '—'}</td>
            </tr>
            <tr>
              <th>Color</th>
              <td>
                {phantom.color
                  ? <Badge bg="dark">{phantom.color}</Badge>
                  : '—'}
              </td>
            </tr>
            <tr>
              <th>Density (g/cm3)</th>
              <td>{phantom.density || '—'}</td>
            </tr>
            <tr>
              <th>Drum Material</th>
              <td>{phantom.drum_material || '—'}</td>
            </tr>
            <tr>
              <th>Drum Diameter (mm)</th>
              <td>{phantom.drum_diameter || '—'}</td>
            </tr>
            <tr>
              <th>Source Job</th>
              <td className="font-monospace">{phantom.sourceJobId || '—'}</td>
            </tr>
            <tr>
              <th>Source Token</th>
              <td className="font-monospace">{phantom.sourceTokenId || '—'}</td>
            </tr>
            <tr>
              <th>Note</th>
              <td>{phantom.note || '—'}</td>
            </tr>
            <tr>
              <th>Created</th>
              <td>{fmtDate(phantom.createdAt)}</td>
            </tr>
            {Array.isArray(phantom.reservedBy) && phantom.reservedBy.length > 0 && (
              <tr>
                <th>Reserved By</th>
                <td>
                  {phantom.reservedBy.map((r, i) => (
                    <div key={i} className="mb-1">
                      <Badge bg="warning" text="dark" className="me-1">
                        {r.jobCode || r.jobId?.slice(0, 8)}
                      </Badge>
                      <span className="text-muted fs-10">
                        {r.productLabel || '—'} — {fmt(r.usedLength)}m — {fmtDate(r.reservedAt)}
                      </span>
                    </div>
                  ))}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
        >
          {deleteMutation.isPending ? 'Deleting...' : 'Delete Phantom'}
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PhantomTokenDetailModal;
